import { ButtonImpression } from "@jmc/core/src/components/ButtonImpression/index";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSSocialMediaProps } from "@jwm/types/CMSSocialMediaProps";
import React from "react";
import { useTranslation } from "react-i18next";
import {
    EmailIcon,
    EmailShareButton,
    FacebookIcon,
    FacebookShareButton,
    LinkedinIcon,
    LinkedinShareButton,
    TwitterShareButton,
    XIcon,
} from "react-share";

import emailIcon from "../../../static/svgs/social-media-icons/email.svg";
import facebookIcon from "../../../static/svgs/social-media-icons/facebook.svg";
import linkedinIcon from "../../../static/svgs/social-media-icons/linkedin.svg";
import xIcon from "../../../static/svgs/social-media-icons/x.svg";
import style from "./style.module.scss";

export interface PropTypes {
    socialmediadata: CMSSocialMediaProps;
    placement?: string;
}

type PlatformType = CMSSocialMediaProps["platforms"][0];

export const SocialMediaShare: React.FC<PropTypes> = ({
    socialmediadata: { platforms, title },
    placement = "none",
}: PropTypes): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    const isBrowser = typeof document !== "undefined";
    const targetUrl = isBrowser ? window?.location?.origin + window?.location?.pathname : "";

    const facebook: PlatformType["facebook"] = platforms.find((platform) => platform.facebook)?.facebook;
    const twitter: PlatformType["twitter"] = platforms.find((platform) => platform.twitter)?.twitter;
    const linkedin: PlatformType["linkedin"] = platforms.find((platform) => platform.linkedin)?.linkedin;
    const email: PlatformType["email"] = platforms.find((platform) => platform.email)?.email;
    const emailTitle = email?.subject;
    const emailBody = `${targetUrl} ${email?.body}`;
    const emailShareUrl = `mailto:?subject=${emailTitle}&body=${emailBody}`;

    const hasPlatforms: boolean = !!facebook || !!twitter || !!linkedin || !!email;

    const { t } = useTranslation();

    // Title is a new field. If it's null or undefined, we use a fallback, so the editors don't have to fill it.
    // Otherwise it's been filled manually and we use the given value, and it can be blank.
    const fallbackTitle = title == null ? t("Share it with your friends!", { ns: "common" }) : title;

    return (
        <div className={style.socialMedia_container}>
            {!jnjFullBranded && fallbackTitle && <div className={style.mediashare_heading}>{fallbackTitle}</div>}
            {jnjFullBranded && fallbackTitle && (
                <Typography variant="h2" color="dark" size={jnjFullBranded ? "xl" : "2xl"} font="title" weight={"400"}>
                    {fallbackTitle}
                </Typography>
            )}

            {hasPlatforms && (
                <div className={style.media_btn_container}>
                    {facebook?.share && (
                        <ButtonImpression
                            name={"share facebook"}
                            category={"social sharing"}
                            placement={placement}
                            targeturl={"https://www.facebook.com/sharer/sharer.php?u=" + targetUrl}
                            targettype={"external"}
                        >
                            <div className={style.platform} key="facebook" aria-label="facebook" title="facebook">
                                <FacebookShareButton url={targetUrl} quote={facebook?.message ? facebook?.message : ""}>
                                    {jnjFullBranded ? (
                                        <img
                                            src={facebookIcon}
                                            alt={"facebook"}
                                            data-test-id={`SocialMediaShare.Facebook`}
                                        />
                                    ) : (
                                        <FacebookIcon size={40} borderRadius={4} />
                                    )}
                                </FacebookShareButton>
                            </div>
                        </ButtonImpression>
                    )}
                    {twitter?.share && (
                        <ButtonImpression
                            name={"share X"}
                            category={"social sharing"}
                            placement={placement}
                            targeturl={"https://twitter.com/share?url=" + targetUrl}
                            targettype={"external"}
                        >
                            <div className={style.platform} key="X" aria-label="X" title="X">
                                <TwitterShareButton url={targetUrl} title={twitter?.message ? twitter?.message : "X"}>
                                    {jnjFullBranded ? (
                                        <img src={xIcon} alt={"X"} data-test-id={`SocialMediaShare.X`} />
                                    ) : (
                                        <XIcon size={40} borderRadius={4} />
                                    )}
                                </TwitterShareButton>
                            </div>
                        </ButtonImpression>
                    )}
                    {linkedin?.share && (
                        <ButtonImpression
                            name={"share linkedin"}
                            category={"social sharing"}
                            placement={placement}
                            targeturl={"https://linkedin.com/shareArticle?url=" + targetUrl}
                            targettype={"external"}
                        >
                            <div className={style.platform} key="linkedin" aria-label="linkedin" title="linkedin">
                                <LinkedinShareButton url={targetUrl}>
                                    {jnjFullBranded ? (
                                        <img
                                            src={linkedinIcon}
                                            alt={"linkedin"}
                                            data-test-id={`SocialMediaShare.Linkedin`}
                                        />
                                    ) : (
                                        <LinkedinIcon size={40} borderRadius={4} />
                                    )}
                                </LinkedinShareButton>
                            </div>
                        </ButtonImpression>
                    )}
                    {email?.share && (
                        <ButtonImpression
                            name={"share email"}
                            category={"social sharing"}
                            placement={placement}
                            targeturl={emailShareUrl}
                            targettype={"external"}
                        >
                            <div className={style.platform} key="email" aria-label="email" title="email">
                                <EmailShareButton url={emailBody} subject={emailTitle}>
                                    {jnjFullBranded ? (
                                        <img src={emailIcon} alt={"email"} data-test-id={`SocialMediaShare.Email`} />
                                    ) : (
                                        <EmailIcon size={40} borderRadius={4} />
                                    )}
                                </EmailShareButton>
                            </div>
                        </ButtonImpression>
                    )}
                </div>
            )}
        </div>
    );
};

export default SocialMediaShare;
